import { Loader } from "@circle-react-uikit/Loader";
import type {
  FlaggedContentReport,
  FlaggedContentableType,
} from "@circle-react/types";
import { Typography } from "@circleco/compass/components/Typography";
import InfiniteScroll from "react-infinite-scroll-component";

import { t } from "@/i18n-js/instance";
import { ReportItem } from "@/react/components/SettingsApp/Moderation/ReportFlaggedContentModal/ReportItem";
import {
  CHAT_ROOM_MESSAGE_TYPE,
  CONNECT_CONNECTION_TYPE,
} from "@/react/types/FlaggedContent";

import { ReportSection } from "./ReportSection";

export interface ReportsListProps {
  currentReportFallback?: FlaggedContentReport;
  currentReportId?: number;
  hasNextPage?: boolean;
  isLoading?: boolean;
  onLoadMore?: () => void;
  reports?: FlaggedContentReport[];
  type?: FlaggedContentableType;
}

const i18nRoot = "settings.moderation.review_flagged_content_modal";
const SCROLLABLE_TARGET_ID = "reports-list-scroll-container";

export const ReportsList = ({
  currentReportFallback,
  currentReportId,
  hasNextPage = false,
  isLoading = false,
  onLoadMore,
  reports = [],
  type,
}: ReportsListProps) => {
  if (isLoading && reports.length === 0) {
    return (
      <div className="flex items-center justify-center px-8 py-12">
        <Loader center />
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="flex flex-col gap-6 pt-6">
        <Typography variant="label-sm">{t([i18nRoot, "empty"])}</Typography>
      </div>
    );
  }

  const shouldUseThisReportOtherReportsLayout =
    type === CONNECT_CONNECTION_TYPE || type === CHAT_ROOM_MESSAGE_TYPE;

  if (shouldUseThisReportOtherReportsLayout) {
    const currentReport =
      reports.find(r => r.id === currentReportId) ?? currentReportFallback;
    const otherReports = reports.filter(r => r.id !== currentReportId);

    return (
      <div
        id={SCROLLABLE_TARGET_ID}
        className="flex max-h-96 flex-col gap-6 overflow-y-auto pt-6"
      >
        <ReportSection
          title={t([i18nRoot, "this_report"])}
          items={currentReport ? [currentReport] : []}
        />
        {otherReports.length > 0 && (
          <InfiniteScroll
            style={{ overflow: "initial" }}
            scrollThreshold={0.8}
            next={() => onLoadMore?.()}
            hasMore={hasNextPage}
            loader={<Loader center />}
            dataLength={otherReports.length}
            scrollableTarget={SCROLLABLE_TARGET_ID}
          >
            <ReportSection
              title={t([i18nRoot, "other_reports"])}
              items={otherReports}
            />
          </InfiniteScroll>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pt-6">
      {reports.map(report => (
        <ReportItem key={report.id} {...report} />
      ))}
    </div>
  );
};
